import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { customSelectChevronUrlClass } from './svgClasses';

const controlBase =
  'block w-full appearance-none bg-clip-padding px-5 py-2.5 text-base font-normal leading-normal text-gray-700 transition-colors duration-150 ease-in-out focus:outline-none';

const controlBg =
  'bg-gray-100 focus:bg-gray-100 disabled:bg-gray-200 read-only:bg-gray-200';

const controlBorder =
  'rounded border border-gray-500 focus:border-primary';

const controlHeight = 'h-11';


export const Input = forwardRef(function Input(
  { className, invalid, valid, type = 'text', ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      type={type}
      {...rest}
      className={classNames(
        controlBase,
        controlBg,
        controlBorder,
        controlHeight,
        invalid && 'border-danger',
        valid && 'border-success',
        className
      )}
    />
  );
});

export const Textarea = forwardRef(function Textarea(
  { className, invalid, valid, ...rest },
  ref
) {
  return (
    <textarea
      ref={ref}
      {...rest}
      className={classNames(
        controlBase,
        controlBg,
        controlBorder,
        invalid && 'border-danger',
        valid && 'border-success',
        className
      )}
    />
  );
});

export const Select = forwardRef(function Select(
  { className, invalid, valid, children, ...rest },
  ref
) {
  return (
    <select
      ref={ref}
      {...rest}
      className={classNames(
        'block w-full h-11 rounded border border-gray-500 focus:border-primary bg-gray-100 appearance-none pl-5 pr-9 py-2.5 align-middle text-base leading-normal text-gray-700 focus:outline-none bg-[length:8px_10px] bg-[right_0.75rem_center] bg-no-repeat',
        customSelectChevronUrlClass,
        invalid && 'border-danger',
        valid && 'border-success',
        className
      )}
    >
      {children}
    </select>
  );
});

export function FormGroup({ className, tag: Tag = 'div', children, ...rest }) {
  return (
    <Tag {...rest} className={classNames('mb-4', className)}>
      {children}
    </Tag>
  );
}

export function FormText({ className, tag: Tag = 'small', children, ...rest }) {
  return (
    <Tag {...rest} className={classNames('mt-1 block text-[80%]', className)}>
      {children}
    </Tag>
  );
}

export function InvalidFeedback({ className, children, ...rest }) {
  return (
    <div
      {...rest}
      className={classNames('mt-1 w-full text-[80%] text-danger', className)}
    >
      {children}
    </div>
  );
}

export function InputGroup({ className, tag: Tag = 'div', children, ...rest }) {
  return (
    <Tag
      {...rest}
      className={classNames(
        'relative flex w-full flex-wrap items-stretch',
        '[&>input]:relative [&>input]:w-[1%] [&>input]:min-w-0 [&>input]:flex-1',
        className
      )}
    >
      {children}
    </Tag>
  );
}

export function InputGroupText({ className, tag: Tag = 'span', children, ...rest }) {
  return (
    <Tag
      {...rest}
      className={classNames(
        'flex items-center whitespace-nowrap rounded border border-gray-500 bg-gray-200 px-5 py-2.5 text-center text-base font-normal leading-normal text-gray-700',
        className
      )}
    >
      {children}
    </Tag>
  );
}

export function Label({ className, tag: Tag = 'label', children, ...rest }) {
  return (
    <Tag {...rest} className={classNames('mb-2 inline-block', className)}>
      {children}
    </Tag>
  );
}

export default Input;
